import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  APP_ENFORCED_SCOPES,
  fetchRateLimitStats,
  listRateLimitEvents,
  listRateLimitPolicies,
  RATE_LIMIT_SCOPES,
  RATE_LIMIT_SUBJECTS,
  saveRateLimitPolicy,
  scopeMeta,
  simulateLimitCheck,
  subjectMeta,
  toggleRateLimitPolicy,
  type CoreRateLimitPolicy,
  type RateLimitEvent,
} from '@/lib/coreRateLimit';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function RateLimitsTab({ org }: Props) {
  const [policies, setPolicies] = useState<CoreRateLimitPolicy[]>([]);
  const [events, setEvents] = useState<RateLimitEvent[]>([]);
  const [stats, setStats] = useState({ total: 0, enabled: 0, scopes: 0, blocked24h: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [editLimit, setEditLimit] = useState<Record<number, string>>({});
  const [form, setForm] = useState({ scope: 'api', subjectType: 'tenant', limit: 60 });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [p, e, s] = await Promise.all([
        listRateLimitPolicies(org.id),
        listRateLimitEvents(org.id),
        fetchRateLimitStats(org.id),
      ]);
      setPolicies(p);
      setEvents(e);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const onSave = async (p: CoreRateLimitPolicy) => {
    const raw = editLimit[p.id];
    const limit = Number(raw);
    if (!raw || !Number.isFinite(limit) || limit <= 0) { setError('La limite doit être un nombre positif.'); return; }
    setBusy(p.id);
    setError('');
    try {
      await saveRateLimitPolicy({
        organizationId: org.id, tenantId: org.tenant_id,
        scope: p.scope, subjectType: p.subject_type, limitPerMin: limit, windowSeconds: p.window_seconds,
      });
      setEditLimit((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Enregistrement impossible.');
    } finally {
      setBusy(null);
    }
  };

  const onToggle = async (p: CoreRateLimitPolicy) => {
    setBusy(p.id);
    try { await toggleRateLimitPolicy(p.id, !p.enabled); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  const onAdd = async () => {
    if (!form.limit || form.limit <= 0) { setError('La limite doit être un nombre positif.'); return; }
    setSaving(true);
    setError('');
    try {
      await saveRateLimitPolicy({
        organizationId: org.id, tenantId: org.tenant_id, scope: form.scope, subjectType: form.subjectType, limitPerMin: form.limit,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Rate limiting</span> : protège l’API, la recherche,
        l’authentification, les AI Tools, les actions et les webhooks. Les limites s’adaptent par utilisateur,
        entreprise, endpoint, outil ou adresse IP.
      </p>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
          <i className="ri-shield-star-line text-primary-600"></i>Appliqué côté application
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">Par utilisateur</span>
        </h4>
        <p className="text-[11px] text-foreground-500 mb-3">
          En plus de l’Edge Function (par clé d’API), ces surfaces vérifient la limite <span className="font-medium text-foreground-600">avant exécution</span> dans l’application, et journalisent chaque passage.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {APP_ENFORCED_SCOPES.map((s) => (
            <div key={s.scope} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className={s.icon}></i></span>
              <p className="text-xs font-medium text-foreground-800 mt-2">{s.label}</p>
              <p className="text-[11px] text-foreground-500">{s.subject}</p>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-shield-check-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Politiques</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-toggle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.enabled}</p>
          <p className="text-xs text-foreground-500">Actives</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-apps-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.scopes}</p>
          <p className="text-xs text-foreground-500">Surfaces protégées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-forbid-2-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.blocked24h}</p>
          <p className="text-xs text-foreground-500">Bloqués (24 h)</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-shield-check-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Politiques de limitation</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : policies.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune politique définie.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {policies.map((p) => {
              const sc = scopeMeta(p.scope);
              const sub = subjectMeta(p.subject_type);
              const used = p.id % 40;
              const sim = simulateLimitCheck(p, used);
              return (
                <div key={`${p.scope}-${p.subject_type}-${p.id}`} className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-700 flex items-center justify-center flex-shrink-0"><i className={sc.icon}></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-800 flex items-center gap-2">
                        {sc.label}
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900">{sub.label}</span>
                        {p.organization_id == null && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">plateforme</span>}
                      </p>
                      <p className="text-[11px] text-foreground-400">{p.limit_per_min} req / {p.window_seconds}s{p.burst ? ` · burst ${p.burst}` : ''}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${p.enabled ? 'bg-secondary-100 text-secondary-900' : 'bg-background-100 text-foreground-500'}`}>{p.enabled ? 'Active' : 'Désactivée'}</span>
                  </div>
                  <div className="mt-2 pl-12 flex items-center gap-2 flex-wrap">
                    <div className="w-28 h-1.5 rounded-full bg-background-200/70 overflow-hidden">
                      <div className={`h-full rounded-full ${sim.utilization > 80 ? 'bg-accent-500' : 'bg-primary-500'}`} style={{ width: `${sim.utilization}%` }}></div>
                    </div>
                    <span className="text-[10px] text-foreground-400">{sim.utilization}% utilisé (démo)</span>
                    <input
                      type="number" min={1}
                      value={editLimit[p.id] ?? String(p.limit_per_min)}
                      onChange={(e) => setEditLimit((prev) => ({ ...prev, [p.id]: e.target.value }))}
                      className="w-24 px-2 py-1 rounded-md border border-background-200/70 bg-background-50 text-xs text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300"
                    />
                    <button onClick={() => onSave(p)} disabled={busy === p.id || editLimit[p.id] === undefined} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Enregistrer</button>
                    <button onClick={() => onToggle(p)} disabled={busy === p.id} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 text-[11px] font-medium whitespace-nowrap cursor-pointer">{p.enabled ? 'Désactiver' : 'Activer'}</button>
                    {busy === p.id && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Nouvelle politique</h4>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select className={INPUT} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                {RATE_LIMIT_SCOPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <select className={INPUT} value={form.subjectType} onChange={(e) => setForm({ ...form, subjectType: e.target.value })}>
                {RATE_LIMIT_SUBJECTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <input type="number" min={1} className={INPUT} placeholder="Limite par minute" value={form.limit} onChange={(e) => setForm({ ...form, limit: Number(e.target.value) })} />
            <button onClick={onAdd} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Ajouter
            </button>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Événements récents</h4>
          </div>
          {events.length === 0 ? (
            <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun passage enregistré.</p>
          ) : (
            <div className="divide-y divide-background-200/60 max-h-72 overflow-y-auto">
              {events.slice(0, 30).map((ev) => (
                <div key={ev.id} className="px-4 py-2.5 flex items-center gap-3">
                  <i className={ev.allowed ? 'ri-checkbox-circle-line text-secondary-600' : 'ri-forbid-2-line text-accent-600'}></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-foreground-700 truncate">{scopeMeta(ev.scope || '').label} · {subjectMeta(ev.subject_type || '').label}</p>
                    <p className="text-[10px] text-foreground-400 truncate">{ev.endpoint || ev.subject_key || '—'} · {ev.created_at ? new Date(ev.created_at).toLocaleString('fr-FR') : ''}</p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${ev.allowed ? 'bg-secondary-100 text-secondary-900' : 'bg-accent-100 text-accent-900'}`}>{ev.allowed ? 'Autorisé' : 'Bloqué'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}